/**
 * "Recommended interviewer" pill shown under the interviewer select in
 * manual one-on-one create mode. Ported from the legacy
 * SchInterviewerFieldAutoRecommend block — clicking auto-fills the
 * interviewer field with the recommended user.
 *
 * Layout (matches legacy):
 *   ┌──────────────────────────────────┐
 *   │  ✨  Recommended                 │
 *   │     Based on interview load…     │
 *   │                                  │
 *   │  CLICK TO SELECT THIS INTERVIEWER│
 *   │  [  avatar  Jane Cooper  ]       │
 *   └──────────────────────────────────┘
 *   ────────────  OR  ─────────────
 *   [ Select interviewer           ▼ ]   ← rendered by the schema
 */
import { Box, Divider, Flex, Text } from '@chakra-ui/react';
import { Star06 } from '@kula-ai/icons/solid/Star06';

export interface RecommendedUser {
  id: number | string;
  name?: string;
  avatar?: string;
}

interface RecommendedInterviewerPillProps {
  user: RecommendedUser;
  onSelect: (_user: RecommendedUser) => void;
}

export const RecommendedInterviewerPill = ({
  user,
  onSelect,
}: RecommendedInterviewerPillProps) => {
  const label = user.name ?? `User ${user.id}`;
  return (
    <Box mb="-18px">
      <Flex flexDir="column" gap="3" bg="primary-25" borderRadius="md" p="3">
        {/* Title + subtitle */}
        <Flex flexDir="column" gap="1">
          <Flex align="center" gap="2">
            <Star06
              width={16}
              height={16}
              color="var(--kula-colors-primary-600)"
            />
            <Text fontSize="sm" fontWeight="semibold" color="primary-600">
              Recommended
            </Text>
          </Flex>
          <Text fontSize="sm" color="secondary-700">
            Based on interview load and availability
          </Text>
        </Flex>

        {/* Clickable chip section */}
        <Flex flexDir="column" gap="1.5">
          <Text
            fontSize="xs"
            color="secondary-500"
            textTransform="uppercase"
            letterSpacing="0.04em"
            fontWeight="medium"
          >
            Click to select this interviewer
          </Text>
          <Flex
            as="button"
            type="button"
            align="center"
            gap="2"
            bg="white"
            border="1px solid"
            borderColor="border-color"
            borderRadius="md"
            px="3"
            py="2"
            w="fit-content"
            cursor="pointer"
            _hover={{ bg: 'secondary-25' }}
            onClick={() => onSelect(user)}
            aria-label={`Select ${label}`}
          >
            {user.avatar && (
              <img
                src={user.avatar}
                alt=""
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  objectFit: 'cover',
                }}
              />
            )}
            <Text fontSize="sm" fontWeight="medium" color="secondary-900">
              {label}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      {/* OR divider — visually separates recommendation from the Select below. */}
      <Flex align="center" gap="3" my="3">
        <Divider flex="1" borderColor="border-color" />
        <Text
          fontSize="xs"
          color="secondary-500"
          textTransform="uppercase"
          fontWeight="medium"
          letterSpacing="0.08em"
        >
          OR
        </Text>
        <Divider flex="1" borderColor="border-color" />
      </Flex>
    </Box>
  );
};
